
import React, { useState } from 'react';
import { authService } from '@/src/services/authService';
import { useAuth } from '@/src/contexts/AuthContext';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToSignUp: () => void;
  onRecoverKey: () => void;
  onSuccess: (type: 'individual' | 'company', user: any) => void;
}

const SignInModal: React.FC<Props> = ({ isOpen, onClose, onSwitchToSignUp, onRecoverKey, onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { user } = await authService.signIn(email, password);
      setLoading(false);
      onSuccess(user.type, user.profile || user);
      onClose();
    } catch (err: any) {
      setLoading(false);
      setError(err.message || 'Authentication failed. Please check your credentials.');
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/75 animate-in fade-in duration-200">
      <div 
        className="w-full max-w-md bg-[#3a3430] border border-white/[0.06] rounded-xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[calc(100vh-2rem)] md:max-h-[calc(100vh-4rem)] text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between bg-[#322D29] shrink-0">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase text-[#F26522] tracking-[0.25em] font-heading">Sign In</span>
            <h2 className="text-base font-bold tracking-tight font-heading text-white leading-tight mt-0.5">Connect an Existing Account</h2>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded-lg hover:bg-white/5"
          >
            <i className="fas fa-times text-base"></i>
          </button>
        </header>

        <div className="p-6 space-y-5 flex-1 overflow-y-auto custom-scrollbar">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">User Email</label>
              <div className="relative">
                <i className="fas fa-envelope absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs"></i>
                <input 
                  type="email"
                  required
                  placeholder="user@example.com"
                  className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-sm font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <label className="text-[10px] font-bold uppercase tracking-widest text-white/60 font-heading">Password</label>
                <button 
                  type="button" 
                  onClick={onRecoverKey}
                  className="text-[9px] font-bold uppercase text-[#F26522] hover:underline tracking-wider cursor-pointer"
                >
                  Forgot Password?
                </button>
              </div>
              <div className="relative">
                <i className="fas fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-xs"></i>
                <input 
                  type="password"
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 bg-[#322D29] border border-white/[0.08] text-white placeholder-white/30 text-sm font-medium rounded-lg outline-none focus:border-[#F26522] transition-colors font-dm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-[#322D29] border border-red-500/40 rounded-lg flex items-center gap-2.5 animate-in slide-in-from-top-1">
                <i className="fas fa-triangle-exclamation text-red-400 text-xs"></i>
                <span className="text-[10px] font-medium text-red-200 tracking-wide">{error}</span>
              </div>
            )}

            <div className="pt-2">
              <button 
                type="submit"
                disabled={loading}
                className="w-full bg-[#F26522] hover:bg-[#F26522]/90 text-white py-3.5 font-bold uppercase text-[11px] tracking-[0.2em] rounded-lg transition-colors font-heading active:scale-[0.99] disabled:opacity-50 flex items-center justify-center gap-2.5 cursor-pointer"
              >
                {loading ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin text-xs"></i>
                    Signing In...
                  </>
                ) : (
                  <>
                    <i className="fas fa-key text-xs"></i>
                    Connect an Existing Account
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="relative pt-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/[0.06]"></div>
            </div>
            <div className="relative flex justify-center text-[9px] uppercase font-bold tracking-[0.2em]">
              <span className="bg-[#3a3430] px-3 text-white/40">New to eNvesta AI?</span>
            </div>
          </div>

          <button 
            type="button"
            onClick={onSwitchToSignUp}
            className="w-full py-3 border border-white/[0.1] text-[10px] font-bold uppercase tracking-[0.15em] text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors cursor-pointer"
          >
            Create an Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignInModal;
